import { Star } from 'lucide-react';
import { cn } from '@/lib/utils';
import { PLATFORM_CAPABILITIES, type ReviewPlatform } from '@/types/review-platform';

/**
 * Brand marks for the review platforms.
 *
 * Follows the rules `settings-integrations/BrandLogo.tsx` set for Connected
 * Apps, because these two directories sit two tabs apart and must read as one
 * system: every mark is fitted to the same box, nothing sets its own size, and
 * the artwork is served from our own `public/` so the page makes no
 * third-party asset request and survives a corporate proxy.
 *
 * The one departure is the plate. Connected Apps drops it because those five
 * marks each carry their own ground; here Google's four-colour G is drawn for
 * white and would sink into a tinted card unaided, so all four sit on the same
 * neutral tile. A shared plate is also what stops a wide mark and a round mark
 * from reading at different weights down a column.
 *
 * Provenance, since a mark that is quietly wrong is worse than no mark:
 *
 *   • google.svg    — the standard four-colour G, drawn on white.
 *   • facebook.svg  — the two-tone f roundel; carries its own blue ground.
 *   • yelp.svg      — simple-icons' Yelp burst (CC0 icon data), filled Yelp red.
 *   • thumbtack.svg — simple-icons' Thumbtack mark (CC0), filled Thumbtack blue.
 *     `fill-rule="evenodd"` is load bearing on this one: the tack is knocked
 *     out of a disc, and under the default nonzero rule it fills to a blob.
 *
 * Marks are used nominatively, to identify the service each row connects to.
 *
 * BBB has no entry on purpose — it is no longer offered (see `REVIEW_PLATFORMS`)
 * — so a stored BBB row falls through to the neutral glyph rather than crashing.
 */
const ARTWORK: Partial<Record<ReviewPlatform, string>> = {
  GOOGLE: '/brand/review-platforms/google.svg',
  FACEBOOK: '/brand/review-platforms/facebook.svg',
  YELP: '/brand/review-platforms/yelp.svg',
  THUMBTACK: '/brand/review-platforms/thumbtack.svg',
};

export function ReviewPlatformLogo({
  platform,
  className,
}: {
  platform: ReviewPlatform;
  className?: string;
}) {
  const src = ARTWORK[platform];
  const label = PLATFORM_CAPABILITIES[platform]?.label ?? platform;

  return (
    <span
      data-testid={`review-platform-logo-${platform}`}
      className={cn(
        'flex size-10 shrink-0 items-center justify-center rounded-lg border border-border/70 bg-background shadow-xs',
        className,
      )}
    >
      {src ? (
        <img
          src={src}
          alt={label}
          loading="lazy"
          decoding="async"
          className="size-[22px] object-contain"
        />
      ) : (
        <Star className="size-5 text-muted-foreground" aria-label={label} />
      )}
    </span>
  );
}
